import React, { useMemo } from "react";
import { motion } from "motion/react";
import {
  Filter,
  CheckCircle,
  AlertCircle,
  Clock,
  CreditCard,
  Smartphone,
  Package,
  X,
} from "lucide-react";

const SubscriptionFilters = ({
  statusFilter,
  planFilter,
  networkFilter,
  packageFilter,
  onStatusChange,
  onPlanChange,
  onNetworkChange,
  onPackageChange,
  onClearFilters,
  subscriptions,
}) => {
  // Get unique plans from data
  const availablePlans = useMemo(() => {
    const plans = [...new Set(subscriptions.map((sub) => sub.plan_name))];
    return plans.filter((plan) => plan).sort();
  }, [subscriptions]);

  // Get unique packages from data
  const availablePackages = useMemo(() => {
    const packages = [...new Set(subscriptions.map((sub) => sub.package_name))];
    return packages.filter((pkg) => pkg).sort();
  }, [subscriptions]);

  const statusOptions = [
    // { value: "all", label: "All Status", icon: Filter, color: "text-gray-500" },
    { value: "paid", label: "Paid", icon: CheckCircle, color: "text-red-600" },
    {
      value: "unpaid",
      label: "Unpaid",
      icon: AlertCircle,
      color: "text-orange-600",
    },
    {
      value: "trial",
      label: "Free Trial",
      icon: Clock,
      color: "text-purple-600",
    },
  ];

  const planOptions = [
    {
      value: "all",
      label: "All Plans",
      icon: CreditCard,
      color: "text-gray-500",
    },
    ...availablePlans.map((plan) => ({
      value: plan,
      label: plan,
      icon: CreditCard,
      color: "text-blue-600",
    })),
  ];

  // Network codes as used by the payments/export API
  const networkOptions = [
    { value: "all", label: "All Networks", icon: Smartphone, color: "text-gray-500" },
    { value: "mtn", label: "MTN", icon: Smartphone, color: "text-yellow-600" },
    { value: "vdf", label: "Telecel", icon: Smartphone, color: "text-red-600" },
    { value: "Atl", label: "AirtelTigo", icon: Smartphone, color: "text-blue-600" },
  ];

  const packageOptions = [
    {
      value: "all",
      label: "All Packages",
      icon: Package,
      color: "text-gray-500",
    },
    ...availablePackages.map((pkg) => ({
      value: pkg,
      label: pkg,
      icon: Package,
      color: "text-blue-600",
    })),
  ];

  const hasActiveFilters =
    statusFilter !== "all" ||
    planFilter !== "all" ||
    networkFilter !== "all" ||
    packageFilter !== "all";

  return (
    <div className="space-y-4">
      {/* Status Filter */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">
            Payment Status
          </h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 gap-2">
            {statusOptions.map((option) => (
              <motion.button
                key={option.value}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onStatusChange(option.value)}
                className={`flex items-center gap-2 p-3 text-sm font-medium rounded-lg border transition-all duration-200 ${
                  statusFilter === option.value
                    ? "bg-purple-50 border-purple-200 text-purple-700"
                    : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <option.icon className={`w-4 h-4 ${option.color}`} />
                <span className="truncate">{option.label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* Network Filter */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">
            Network
          </h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 gap-2">
            {networkOptions.map((option) => (
              <motion.button
                key={option.value}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onNetworkChange(option.value)}
                className={`flex items-center gap-2 p-3 text-sm font-medium rounded-lg border transition-all duration-200 ${
                  networkFilter === option.value
                    ? "bg-purple-50 border-purple-200 text-purple-700"
                    : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <option.icon className={`w-4 h-4 ${option.color}`} />
                <span className="truncate">{option.label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* Plan Filter */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">
            Subscription Plans
          </h3>
        </div>
        <div className="p-4">
          <div className="space-y-2">
            {planOptions.map((option) => (
              <motion.button
                key={option.value}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => onPlanChange(option.value)}
                className={`flex items-center gap-3 w-full p-3 text-sm font-medium rounded-lg border transition-all duration-200 ${
                  planFilter === option.value
                    ? "bg-purple-50 border-purple-200 text-purple-700"
                    : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <option.icon className={`w-4 h-4 ${option.color}`} />
                <span className="flex-1 text-left truncate">
                  {option.label}
                </span>
                {planFilter === option.value && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-2 h-2 bg-purple-600 rounded-full"
                  />
                )}
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* Package Filter */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">
            Package
          </h3>
        </div>
        <div className="p-4">
          <div className="space-y-2">
            {packageOptions.map((option) => (
              <motion.button
                key={option.value}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => onPackageChange(option.value)}
                className={`flex items-center gap-3 w-full p-3 text-sm font-medium rounded-lg border transition-all duration-200 ${
                  packageFilter === option.value
                    ? "bg-purple-50 border-purple-200 text-purple-700"
                    : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <option.icon className={`w-4 h-4 ${option.color}`} />
                <span className="flex-1 text-left truncate">
                  {option.label}
                </span>
                {packageFilter === option.value && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-2 h-2 bg-purple-600 rounded-full"
                  />
                )}
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onClearFilters}
          className="flex gap-2 justify-center items-center p-3 w-full text-sm font-medium text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
        >
          <X className="w-4 h-4" />
          Clear All Filters
        </motion.button>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="p-3 bg-purple-50 rounded-lg border border-purple-200"
        >
          <div className="flex flex-wrap gap-2">
            <span className="text-xs font-medium text-purple-700">
              Active filters:
            </span>
            {statusFilter !== "all" && (
              <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-md">
                {statusOptions.find((opt) => opt.value === statusFilter)?.label}
                <button
                  onClick={() => onStatusChange("all")}
                  className="ml-1 hover:text-purple-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {planFilter !== "all" && (
              <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-md">
                {planOptions.find((opt) => opt.value === planFilter)?.label}
                <button
                  onClick={() => onPlanChange("all")}
                  className="ml-1 hover:text-purple-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {networkFilter !== "all" && (
              <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-md">
                {networkOptions.find((opt) => opt.value === networkFilter)?.label}
                <button
                  onClick={() => onNetworkChange("all")}
                  className="ml-1 hover:text-purple-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {packageFilter !== "all" && (
              <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-md">
                {packageOptions.find((opt) => opt.value === packageFilter)?.label}
                <button
                  onClick={() => onPackageChange("all")}
                  className="ml-1 hover:text-purple-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SubscriptionFilters;
